import { useCallback, useRef, useState } from "react";
import { Camera, Trash2, RotateCw, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export interface UploadedPhoto {
  id: string;
  url: string;
}

interface PendingPhoto {
  id: string;
  previewUrl: string;
  base64: string;
  status: "uploading" | "failed";
  error?: string;
}

interface PhotoCaptureProps {
  photos: UploadedPhoto[];
  onUpload: (base64: string) => Promise<void>;
  onDelete: (photoId: string) => Promise<void>;
  minRequired?: number;
  disabled?: boolean;
  className?: string;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read file"));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
    reader.readAsDataURL(file);
  });
}

export function PhotoCapture({
  photos,
  onUpload,
  onDelete,
  minRequired,
  disabled,
  className,
}: PhotoCaptureProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<PendingPhoto[]>([]);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const fileArr = Array.from(files);

      const prepared = await Promise.all(
        fileArr.map(async (file) => {
          const base64 = await fileToBase64(file);
          return {
            id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            previewUrl: URL.createObjectURL(file),
            base64,
            status: "uploading" as const,
          };
        }),
      );

      setPending((p) => [...p, ...prepared]);

      for (const p of prepared) {
        try {
          await onUpload(p.base64);
          setPending((cur) => cur.filter((x) => x.id !== p.id));
          URL.revokeObjectURL(p.previewUrl);
        } catch (err) {
          setPending((cur) =>
            cur.map((x) =>
              x.id === p.id
                ? { ...x, status: "failed", error: err instanceof Error ? err.message : "Upload failed" }
                : x,
            ),
          );
        }
      }
    },
    [onUpload],
  );

  const handleRetry = useCallback(
    async (pendingId: string) => {
      const p = pending.find((x) => x.id === pendingId);
      if (!p) return;
      setPending((cur) =>
        cur.map((x) => (x.id === pendingId ? { ...x, status: "uploading" } : x)),
      );
      try {
        await onUpload(p.base64);
        setPending((cur) => cur.filter((x) => x.id !== pendingId));
        URL.revokeObjectURL(p.previewUrl);
      } catch (err) {
        setPending((cur) =>
          cur.map((x) =>
            x.id === pendingId
              ? { ...x, status: "failed", error: err instanceof Error ? err.message : "Upload failed" }
              : x,
          ),
        );
      }
    },
    [pending, onUpload],
  );

  const handleCancelPending = useCallback((pendingId: string) => {
    setPending((cur) => {
      const p = cur.find((x) => x.id === pendingId);
      if (p) URL.revokeObjectURL(p.previewUrl);
      return cur.filter((x) => x.id !== pendingId);
    });
  }, []);

  const total = photos.length;
  const needed = minRequired ? Math.max(0, minRequired - total) : 0;

  return (
    <div className={cn("space-y-3", className)}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className="flex w-full min-h-20 flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-muted-foreground/40 bg-muted/20 py-3 text-muted-foreground active:bg-muted/40 disabled:opacity-50"
        aria-label="Add photo"
      >
        <Camera className="h-6 w-6" />
        <span className="text-xs font-medium">Add photo</span>
      </button>

      <div className="grid grid-cols-3 gap-2">
        {photos.map((photo) => (
          <div
            key={photo.id}
            className="relative aspect-square overflow-hidden rounded-md border bg-muted"
          >
            <img
              src={photo.url}
              alt="Uploaded"
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <button
              type="button"
              disabled={disabled}
              onClick={() => onDelete(photo.id)}
              className="absolute top-1 right-1 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white active:bg-black/80 disabled:opacity-50"
              aria-label="Delete photo"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}

        {pending.map((p) => (
          <div
            key={p.id}
            className="relative aspect-square overflow-hidden rounded-md border bg-muted"
          >
            <img
              src={p.previewUrl}
              alt="Uploading"
              className="h-full w-full object-cover opacity-60"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/40 text-white">
              {p.status === "uploading" ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-[10px] font-medium">Uploading…</span>
                </>
              ) : (
                <>
                  <span className="text-[10px] font-medium">Failed</span>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => handleRetry(p.id)}
                      className="inline-flex h-8 min-w-11 items-center gap-1 rounded-md bg-white px-2 text-[11px] font-medium text-black active:bg-white/80"
                    >
                      <RotateCw className="h-3 w-3" />
                      Retry
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCancelPending(p.id)}
                      className="inline-flex h-8 items-center rounded-md bg-black/60 px-2 text-[11px] font-medium text-white"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}

      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {minRequired !== undefined && needed > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {needed} more photo{needed === 1 ? "" : "s"} required
        </p>
      )}
    </div>
  );
}

export { PhotoCapture as default };
